import axios, { AxiosError, AxiosProgressEvent } from "axios";
import {
  CreateMailboxReservationRequest,
  SkpEnvironment,
  ErrorMessageBudle,
  MailboxDetails,
  MailboxGroup,
  MailboxReservation,
  PublicProfile,
  ReservationCreationResponse,
  TransferReceipt,
  UpdateReservationRequest,
  UpdateReservationResponse,
  OrganizationEnvironment,
  Transfer,
  PasswordProtectedTransfer,
  ShareLinkResolution,
  ShareLinkFile,
  PublishedSpaceResolution,
  MalwareStatus,
  TransferProtection,
  TransferType,
  TransferDestinationType,
  SpaceInvitationDetails,
  AuthNextResolution,
} from "./entities";
import { applyReservationAccessTokenInterceptor } from "./services/reservationAuth";
import { transformToDateAndUrlType } from "./utils/axios";
import {
  basePathV1,
  basePathV2,
  hostname,
  skalioIDBasePath,
} from "./utils/environment";

const skpServerBaseUrlV1: string = `https://${hostname}${basePathV1}/`;
const skpServerBaseUrlV2: string = `https://${hostname}${basePathV2}/`;

const skalioIdBaseURL: string = `https://${hostname}${skalioIDBasePath}/`;

const driveBasePathV3: string = "/api/drive/v3";
const driveBaseUrlV3: string = `https://${hostname}${driveBasePathV3}/`;

const apiClientV1 = axios.create({
  baseURL: skpServerBaseUrlV1,
});

const apiClientV1WithInterceptor = axios.create({
  baseURL: skpServerBaseUrlV1,
});

const apiClientV2 = axios.create({
  baseURL: skpServerBaseUrlV2,
  withCredentials: true, // Required to handle Set-Cookie headers for session cookies
});

const apiClientV2WithInterceptor = axios.create({
  baseURL: skpServerBaseUrlV2,
  withCredentials: true, // Required to handle Set-Cookie headers for session cookies
});
// Attach reservation auth interceptor for extracting and injecting
// access tokens for reservations from and to requests.
applyReservationAccessTokenInterceptor(apiClientV2);

const apiClientForSkalioId = axios.create({
  baseURL: skalioIdBaseURL,
});

const apiClientForSkalioIdWithInterceptor = axios.create({
  baseURL: skalioIdBaseURL,
});

// Dedicated client for the Drive API. `withCredentials` is required so the
// browser stores and resubmits the HttpOnly guest-token cookie issued by
// `GET /api/drive/v3/get/{shareLinkUid}` on subsequent requests within
// that scope.
const apiClientDriveV3 = axios.create({
  baseURL: driveBaseUrlV3,
  withCredentials: true,
});

//! This axios instance must be used when response data contains timestamp,
//! so as to transform string type timestamp to Date type via interceptor function.
apiClientV1WithInterceptor.interceptors.response.use(transformToDateAndUrlType);

apiClientForSkalioIdWithInterceptor.interceptors.response.use(
  transformToDateAndUrlType
);

apiClientV2WithInterceptor.interceptors.response.use(transformToDateAndUrlType);

// ===================================================================================

type UploadFileChunkProp = {
  reservationUid: string;
  filename: string;
  clientId: string;
  file: File;
  startByte: number;
  endByte: number;
  totalBytes: number;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
};

type DownloadFileProp = {
  transferRecipientId: string;
  objectUid: string;
  fileName: string;
  embed?: boolean;
};

type PreviewFileProp = {
  transferRecipientId?: string;
  objectUid?: string;
};

type UnlockTransferProp = {
  transferRecipientId?: string;
  userInputPassword: string;
};

type ReservationCreationResult = {
  reservation: ReservationCreationResponse;
  reservationToken: string | null;
};

// ===================================================================================

const reservationTokenStore = new Map<string, string>();

const getReservationToken = (reservationUid: string): string | undefined =>
  reservationTokenStore.get(reservationUid);

const resolveVanityLink = async (
  vanityLink: string
): Promise<MailboxDetails> => {
  const response = await apiClientV2.get(`/public/mailbox/${vanityLink}`);
  return response.data;
};

const fetchSkpEnvironment = (): Promise<SkpEnvironment> => {
  return apiClientV1WithInterceptor
    .get("/environment")
    .then((response) => response.data);
};

const fetchOrgEnvironment = (): Promise<OrganizationEnvironment> => {
  return apiClientForSkalioIdWithInterceptor
    .get("/environment")
    .then((response) => response.data);
};

const createMailboxReservation = async (
  key: string,
  request?: CreateMailboxReservationRequest
): Promise<ReservationCreationResult> => {
  const url = `/public/mailbox/${key}`;

  const response =
    request === undefined
      ? await apiClientV2.post(url)
      : await apiClientV2.post(url, request);

  // Axios normalizes header names to lowercase in the browser
  const authHeader: string =
    response.headers?.["authorization"] ?? response.headers?.["Authorization"];

  // Extract reservation token
  const token = authHeader.slice("Bearer ".length).trim();

  // Persist reservation token
  if (token && response.data?.uid) {
    reservationTokenStore.set(response.data.uid, token);
  }

  return {
    reservation: response.data as ReservationCreationResponse,
    reservationToken: token,
  };
};

const updateReservation = async (
  uid: string,
  reservationToken: string,
  request: UpdateReservationRequest
): Promise<UpdateReservationResponse> => {
  const url = `/reservations/${uid}`;
  const headers = { Authorization: `Bearer ${reservationToken}` };

  const response = await apiClientV2.put(url, request, { headers });

  return response.data;
};

const fetchReservation = (
  reservationUid: string
): Promise<MailboxReservation> => {
  return apiClientV2
    .get(`reservations/${reservationUid}`)
    .then((response) => response.data);
};

const confirmReservation = (
  reservationUid: string,
  reservationToken: string
): Promise<ListResponse<TransferReceipt, "transfers">> => {
  return apiClientV2
    .post(
      `reservations/${reservationUid}/confirm`,
      undefined, // no body
      { headers: { Authorization: `Bearer ${reservationToken}` } }
    )
    .then((response) => response.data);
};

const cancelReservation = (reservationUid: string): Promise<void> => {
  return apiClientV2.delete(`/reservations/${reservationUid}`);
};

/**
 * Uploads a file in a multipart/form-data format.
 *
 * @param uploadFileChunkProp
 */
const uploadFileChunk = async ({
  reservationUid,
  filename,
  clientId,
  file,
  startByte,
  endByte,
  totalBytes,
  onUploadProgress,
  signal,
}: UploadFileChunkProp): Promise<void> => {
  const contentRange = `bytes ${startByte}-${endByte - 1}/${totalBytes}`;
  let formData = new FormData();
  formData.append("file", file.slice(startByte, endByte), filename);

  return apiClientV2.post(
    `/reservations/${reservationUid}/files/${clientId}`,
    formData,
    {
      headers: {
        "Content-Range": contentRange,
      },
      onUploadProgress: onUploadProgress,
      signal: signal,
    }
  );
};

/**
 * Checks & returns the size of the already uploaded reserved file.
 *
 * It will throw 404 exception aka Not found exception, if the
 * reserved file, associated with the objectId, is not yet uploaded on
 * the server.
 *
 * @param reservationUid unique id of the reservation.
 * @param clientId  unique id of the file.
 * @returns a promise of size(in bytes) of the reserved file uploaded on server.
 */
const getUploadedFileSize = async ({
  reservationUid,
  clientId,
}: {
  reservationUid: string;
  clientId: string;
}): Promise<number> => {
  return apiClientV2
    .head(`/reservations/${reservationUid}/files/${clientId}`)
    .then((response) => Number(response.headers["content-length"] ?? "0"));
};

type V1User = {
  email: string;
  realname: string;
  uid: string;
};

type V1FileObject = {
  objectId: string;
  name: string;
  size: number;
  url: URL;
  hashsum: string;
  malware: { status: string; text: string | null };
  downloadCounter: number;
  mimeType: string;
};

type V1TransferResponse = {
  recipientId: string;
  sender: V1User;
  receiver: V1User & { type: string };
  uploadTimestamp: Date;
  accessTimestamp: Date | null;
  downloadTimestamp: Date | null;
  expirationTimestamp: Date;
  subject: string;
  description: string;
  signature: string | null;
  priority: number;
  malwareStatus: string;
  bounceReason: string | null;
  totalFileSize: number;
  files: V1FileObject[];
  anon: { email: string; realname: string } | null;
  isProtected: boolean;
  isLocked: boolean;
  isBlocked: boolean;
  isRecipientAuthenticationEnabled: boolean;
};

// V1 uses a sentinel date (before year 2000) to indicate "never accessed". V2 uses null.
const mapAccessTimestamp = (ts: Date | null): Date | null => {
  if (!ts) return null;
  if (ts < new Date("2000-01-01")) return null;
  return ts;
};

const mapV1TransferToTransfer = (v1: V1TransferResponse): Transfer => {
  const protection = v1.isRecipientAuthenticationEnabled
    ? TransferProtection.recipientAuthentication
    : v1.isProtected
    ? TransferProtection.password
    : TransferProtection.none;

  return {
    uid: v1.recipientId,
    type: TransferType.archive,
    protection,
    senderUid: v1.sender.uid,
    senderAddress: v1.sender.email,
    senderName: v1.sender.realname,
    recipientUid: v1.receiver.uid,
    recipientAddress: v1.receiver.email,
    recipientName: v1.receiver.realname,
    destinationType: v1.receiver.type as TransferDestinationType,
    priority: v1.priority,
    anonAddress: v1.anon?.email ?? "",
    anonName: v1.anon?.realname ?? "",
    subject: v1.subject,
    message: v1.description,
    signature: v1.signature,
    uploadedAt: v1.uploadTimestamp,
    accessedAt: mapAccessTimestamp(v1.accessTimestamp),
    lastDownloadAt: v1.downloadTimestamp,
    expiresAt: v1.expirationTimestamp,
    malwareStatus: v1.malwareStatus as MalwareStatus,
    bounceReason: v1.bounceReason,
    locked: v1.isLocked,
    blocked: v1.isBlocked,
    totalSize: v1.totalFileSize,
    objects: v1.files.map((file) => ({
      uid: file.objectId,
      name: file.name,
      contentLength: file.size,
      contentType: file.mimeType,
      malwareStatus: file.malware.status as MalwareStatus,
      malwareText: file.malware.text ?? "",
      hashsum: file.hashsum,
      url: file.url,
      downloadCounter: file.downloadCounter,
      quarantined: false,
    })),
  };
};

/**
 * Fetches a specific transfer according to [transferUid].
 *
 * @param transferUid unique transfer uid.
 * @returns a promise of Transfer or PasswordProtectedTransfer depending on protection.
 */
// https://dev.skalio.net/teambeam/api/v2/#accessing-transfers-transfers-get-3
const fetchTransfer = async (
  transferUid: string
): Promise<Transfer | PasswordProtectedTransfer> => {
  try {
    const response = await apiClientV2WithInterceptor.get(
      `/transfers/${transferUid}`
    );
    return response.data;
  } catch (error) {
    // Error 403138: v2 API does not support this resource (e.g. public archive transfers).
    // Fall back to v1 and map its response shape to the v2 Transfer interface.
    if (
      error instanceof AxiosError &&
      error.response?.data?.error?.code === 403138
    ) {
      const response = await apiClientV1WithInterceptor.get(
        `/transfers/${transferUid}`
      );
      return mapV1TransferToTransfer(response.data as V1TransferResponse);
    }
    throw error;
  }
};

const getFilePreviewUrl = (previewFileProp: PreviewFileProp): URL => {
  return new URL(
    `preview/${previewFileProp.transferRecipientId}/${previewFileProp.objectUid}`,
    skpServerBaseUrlV2
  );
};

const getFilePreviewUrlV1 = (
  transferRecipientId: string,
  fileIndex: number
): URL => {
  return new URL(
    `preview/${transferRecipientId}/${fileIndex}`,
    skpServerBaseUrlV1
  );
};

const getFileDownloadUrl = ({
  transferRecipientId,
  objectUid,
  fileName,
  embed,
}: DownloadFileProp): URL => {
  const encodedFilename = encodeURIComponent(fileName);
  const url = new URL(
    `download/${transferRecipientId}/${objectUid}/${encodedFilename}`,
    skpServerBaseUrlV2
  );
  if (embed) url.search = "embed";

  return url;
};

const getTransferDownloadUrl = (transferRecipientId: string): URL => {
  return new URL(`download/${transferRecipientId}/zip`, skpServerBaseUrlV2);
};

/**
 * Unlocks a password-protected transfer.
 * On success, the response includes a Set-Cookie header with a session cookie
 * that marks the transfer as accessible. The cookie is automatically handled
 * by the browser when withCredentials is true.
 *
 * @param unlockTransferProp
 * @returns a promise that resolves when unlock is successful.
 * After unlock, the transfer should be fetched again using fetchTransfer2
 * to get the full Transfer2 object.
 */
const unlockTransfer2 = (
  unlockTransferProp: UnlockTransferProp
): Promise<void> => {
  return apiClientV2WithInterceptor
    .post(`/transfers/${unlockTransferProp.transferRecipientId}/unlock`, {
      unlockCode: unlockTransferProp.userInputPassword,
    })
    .then(() => {
      // The Set-Cookie header is automatically handled by the browser
      // when withCredentials is true. No need to manually set the cookie.
      // The response body is empty on success (204 No Content or similar).
    });
};

/**
 * Fetches the public profile of any person whose profile is stored in Skalio ID.
 *
 * @param uid
 * @returns a promise of [PublicProfile].
 */
const fetchPublicProfile = (uid: string): Promise<PublicProfile> => {
  return apiClientForSkalioId
    .get(`/public/profile/${uid}`)
    .then((response) => response.data);
};

/**
 * Returns the uri for fetching recipient's avatar.
 *
 * @param uid
 * @param height
 * @returns avatar's uri.
 */
const getAvatarUri = (uid: string, height?: number): string => {
  const heightQuereyParam: string = !height ? `` : `?height=${height}`;
  const url: string = `/public/avatar/${uid}` + heightQuereyParam;

  return apiClientForSkalioId.getUri({
    url: url,
  });
};

const isLegacyUser = async (recipientEmail: string): Promise<boolean> => {
  try {
    await apiClientV1.post(`/auth/exists`, {
      address: recipientEmail,
    });
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return false;
    }
    throw error;
  }
  return true;
};

const isSkalioIdUser = async (recipientEmail: string): Promise<boolean> => {
  try {
    await apiClientForSkalioId.post(`/auth/exists`, {
      address: recipientEmail,
    });
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return false;
    }
    throw error;
  }

  return true;
};

const fetchErrorMessageTranslationBundle = async (
  locale: string
): Promise<ErrorMessageBudle> => {
  return apiClientForSkalioId
    .get(`/i18n/${locale}`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((response) => response.data);
};

// `key` could be vanity name or uid of a mailbox group.
const fetchMailboxGroup = (key: string): Promise<MailboxGroup> => {
  return apiClientV2
    .get(`/public/mailboxgroup/${key}`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((response) => response.data);
};

// ===================================================================================
//                                  Drive share-links
// ===================================================================================

/**
 * Don't reactively re-resolve more than once per this window. The guest token
 * lives ~10 min, so a refresh within the last minute means the cookie is still
 * fresh and we can skip the round-trip.
 */
const GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS = 60_000;
/** Monotonic (`performance.now`) timestamp of the last successful resolve. */
let lastGuestTokenRefreshAt = Number.NEGATIVE_INFINITY;
let guestTokenRefreshInFlight: Promise<boolean> | null = null;

/**
 * Resolves a drive share-link and returns the guest payload
 * (space info, file list and a short-lived JWT guest token).
 *
 * The server also sets an HttpOnly cookie scoped to
 * `/api/drive/v3/get/{shareLinkUid}`, so file download/preview/ZIP requests
 * authenticate automatically — callers do not need to forward the `token`.
 *
 * @param shareLinkUid — NanoID identifying the share-link.
 * @throws AxiosError 404 when the share-link does not exist or has expired.
 */
const resolveShareLink = async (
  shareLinkUid: string
): Promise<ShareLinkResolution> => {
  const response = await apiClientDriveV3.get(`/get/${shareLinkUid}`);
  // Resolving re-sets the guest-token cookie, so any successful resolve (the
  // initial query included) counts as a refresh for the throttle below.
  lastGuestTokenRefreshAt = performance.now();
  return response.data as ShareLinkResolution;
};

/**
 * Single-flight guest-token refresh shared by {@link ensureFreshGuestToken} and
 * {@link refreshGuestTokenNow}: concurrent callers join the one in-flight
 * resolve. It **never rejects**, that is, it resolves to whether the refresh succeeded,
 * so that, each public wrapper applies its own failure policy regardless of which
 * one started (or joined) the flight. A successful resolve advances the
 * throttle clock (in {@link resolveShareLink}); a failure leaves it unmoved so
 * the next caller retries.
 */
const runGuestTokenRefresh = (shareLinkUid: string): Promise<boolean> => {
  if (guestTokenRefreshInFlight) return guestTokenRefreshInFlight;
  guestTokenRefreshInFlight = (async () => {
    try {
      await resolveShareLink(shareLinkUid);
      return true;
    } catch {
      return false;
    } finally {
      guestTokenRefreshInFlight = null;
    }
  })();
  return guestTokenRefreshInFlight;
};

/**
 * Reactively refresh the guest-token cookie by re-resolving the share-link.
 *
 * The cookie authenticates browser-native requests (`<img>` previews,
 * `<a download>`, `<iframe>` media) that never pass through axios, so an
 * interceptor can't transparently retry them. Instead, callers invoke this
 * right before an un-retryable action (a download/ZIP click), and as a safety
 * net when a backgrounded tab becomes visible again, guaranteeing a fresh
 * cookie without a preemptive timer.
 *
 * Best-effort: concurrent calls share one in-flight refresh, calls within
 * {@link GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS} of the last success are skipped,
 * and the outcome is ignored so this **never rejects** — the triggering action
 * surfaces its own error. (Download clicks await this without a try/catch, so
 * it must not throw even when joining a flight a forced refresh started.)
 */
const ensureFreshGuestToken = async (shareLinkUid: string): Promise<void> => {
  // Join an in-flight refresh if one is running; otherwise skip when the cookie
  // was refreshed recently. The outcome is intentionally ignored.
  if (
    !guestTokenRefreshInFlight &&
    performance.now() - lastGuestTokenRefreshAt <
      GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS
  ) {
    return;
  }
  await runGuestTokenRefresh(shareLinkUid);
};

/**
 * Force-refresh the guest-token cookie now, bypassing the
 * {@link GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS} throttle, and surface failure to
 * the caller by **rejecting** (unlike {@link ensureFreshGuestToken}, which
 * swallows it).
 *
 * Used to recover from a `401` on a guest-authenticated request: the cookie has
 * expired, so we must re-resolve regardless of when we last did, then the caller
 * retries the original request. If this rejects (e.g. the share-link itself has
 * expired), the caller surfaces an error dialog. Concurrent calls still share
 * the single in-flight refresh — and since that shared refresh reports its
 * outcome, this rejects too when a flight `ensureFreshGuestToken` started fails.
 */
const refreshGuestTokenNow = async (shareLinkUid: string): Promise<void> => {
  const refreshed = await runGuestTokenRefresh(shareLinkUid);
  if (!refreshed) {
    throw new Error("Failed to refresh the guest-token cookie");
  }
};

/**
 * URL for fetching the avatar of the space a share-link belongs to.
 * Unauthenticated — no guest token required.
 */
const getShareLinkAvatarUrl = (
  shareLinkUid: string,
  height?: number
): string => {
  const base = `/get/${shareLinkUid}/avatar`;
  const url = height ? `${base}?height=${height}` : base;
  return apiClientDriveV3.getUri({ url });
};

/**
 * Fetches the share-link space avatar and returns a local object URL that can
 * be bound to an <img> element.
 *
 * This makes avatar loading explicit via XHR/fetch, which is useful for
 * diagnostics and consistent retry/error handling.
 */
const fetchShareLinkAvatarBlobUrl = async (
  shareLinkUid: string,
  height?: number
): Promise<string> => {
  const response = await apiClientDriveV3.get(`/get/${shareLinkUid}/avatar`, {
    responseType: "blob",
    params: height ? { height } : undefined,
  });

  return URL.createObjectURL(response.data as Blob);
};

/**
 * Fetches the public profile of the user who created a share-link.
 * Uses the skalio-id v3 public persons endpoint (no auth required).
 */
const fetchShareLinkCreatorProfile = async (
  uid: string
): Promise<PublicProfile> => {
  const response = await apiClientForSkalioId.get(`/public/profile/${uid}`);
  return response.data as PublicProfile;
};

/**
 * Fetches the avatar of the share-link creator and returns a local object URL.
 * Only call this when `PublicProfile.hasAvatar` is `true`.
 */
const fetchShareLinkCreatorAvatarBlobUrl = async (
  uid: string,
  height?: number
): Promise<string> => {
  const response = await apiClientForSkalioId.get(`/public/avatar/${uid}`, {
    responseType: "blob",
    params: height ? { height } : undefined,
  });
  return URL.createObjectURL(response.data as Blob);
};

/**
 * URL for downloading a single file referenced by a share-link.
 * Authenticated automatically via the guest-token cookie.
 */
const getShareLinkFileDownloadUrl = (
  shareLinkUid: string,
  metadataUid: string,
  { inline = false }: { inline?: boolean } = {}
): string => {
  const url = `/get/${shareLinkUid}/files/${metadataUid}?inline=${
    inline ? "true" : "false"
  }`;
  return apiClientDriveV3.getUri({ url });
};

/**
 * URL for a preview thumbnail of a shared file.
 * Authenticated automatically via the guest-token cookie.
 */
const getShareLinkFilePreviewUrl = (
  shareLinkUid: string,
  metadataUid: string,
  { width, height }: { width?: number; height?: number } = {}
): string => {
  const base = `/get/${shareLinkUid}/files/${metadataUid}/preview`;
  const query: string[] = [];
  if (width) query.push(`width=${width}`);
  if (height) query.push(`height=${height}`);
  const url = query.length ? `${base}?${query.join("&")}` : base;
  return apiClientDriveV3.getUri({ url });
};

/**
 * URL for a server-rendered thumbnail of a shared file, pre-rendered at 200px
 * (`thumbnail-w200.jpg`). 200 is the largest width the post-processing step
 * offers (https://dev.skalio.net/drive/internal/postprocessing/); grid cards
 * never render narrow enough to need less, so at any real device pixel ratio
 * this single width covers them (the browser scales it to fit). If responsive
 * selection is ever needed, `srcset` is the native tool for it. Authenticated
 * via the guest-token cookie.
 */
const getShareLinkFileThumbnailUrl = (
  shareLinkUid: string,
  metadataUid: string
): string => {
  const url = `/get/${shareLinkUid}/files/${metadataUid}/thumbnail-w200.jpg`;
  return apiClientDriveV3.getUri({ url });
};

/**
 * URL for downloading all files of a share-link as a ZIP archive.
 * Authenticated automatically via the guest-token cookie.
 */
const getShareLinkZipUrl = (shareLinkUid: string): string => {
  const url = `/get/${shareLinkUid}/zip`;
  return apiClientDriveV3.getUri({ url });
};

// ==========================================================================
//                        Published spaces (guest access)
// ==========================================================================

/**
 * Guest-token throttle state for the *published-space* viewer, kept separate
 * from the share-link state above: a browser tab is only ever one or the other,
 * and isolating them keeps the share-link refresh logic untouched.
 */
let lastSpaceGuestTokenRefreshAt = Number.NEGATIVE_INFINITY;
let spaceGuestTokenRefreshInFlight: Promise<boolean> | null = null;
/**
 * Latest issued guest JWT, kept in memory so the snapshot XHR can send it as a
 * bearer header — authenticating it even where the guest cookie is a blocked
 * third-party cookie (e.g. local dev). Thumbnail/avatar loads (via
 * {@link usePreviewImage}) and browser-native `<a download>` requests carry no
 * header and rely on the guest cookie instead, so they authenticate first-party
 * in production but 401 against a cross-origin API host in local dev.
 */
let currentSpaceGuestToken: string | null = null;

/**
 * Raw shape of `GET /api/drive/v3/{spaceUid}` — only the fields the viewer
 * consumes. The space object itself carries no "provider", file count, avatar
 * flag or last-modified timestamp: the provider is the owning organization
 * (resolved from `orgUid`, see {@link fetchSpaceProvider}), the avatar has its
 * own endpoint ({@link getSpaceAvatarUrl}), the file count is derived from the
 * list, and "last updated" is the newest file's `modifiedAt`.
 */
type RawSpaceSnapshot = {
  name?: string;
  /** UID of the owning organization — its name is the space's "provider". */
  orgUid?: string;
  /** Blurhash of the space avatar; null until drive-server populates it. */
  blurhash?: string | null;
  /** When the used-volume figure was last corrected (empty-space "last updated" fallback). */
  usedVolumeCorrectAt?: string;
  files?: ShareLinkFile[];
};

/**
 * The owning organization's display name — shown as a published space's
 * "Bereitgestellt von …" (Provided by) line. The space snapshot exposes only
 * the `orgUid`; the name comes from the public organization profile. Best-effort:
 * resolves to `undefined` (so the header just omits the line) if the lookup fails.
 */
async function fetchSpaceProvider(orgUid: string): Promise<string | undefined> {
  try {
    const { data } = await apiClientForSkalioId.get<{ name?: string }>(
      `/public/organization/${orgUid}`
    );
    return data.name || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Issues a guest access token for a published space and, via `?cookie=true`,
 * sets it as an HttpOnly cookie scoped to the space — so subsequent
 * download/preview requests (including browser-native `<img>`/`<a download>`
 * ones that can't carry an `Authorization` header) authenticate automatically.
 * Returns the raw token (mirrored in the cookie) too.
 *
 * @throws AxiosError 404 when the space is not published (or does not exist).
 */
async function issueSpaceGuestToken(spaceUid: string): Promise<string> {
  const response = await apiClientDriveV3.post(
    `/${spaceUid}/access/guest?cookie=true`
  );
  // Any successful issue re-sets the cookie, so it counts as a refresh.
  lastSpaceGuestTokenRefreshAt = performance.now();
  const token = (response.data as { token: string }).token;
  currentSpaceGuestToken = token;
  return token;
}

/** The latest issued space guest JWT (for bearer-authing fetch/XHR requests). */
function getSpaceGuestToken(): string | null {
  return currentSpaceGuestToken;
}

/**
 * Resolves a published space for the guest viewer: issues the guest token
 * (setting the cookie), fetches the space snapshot, and returns the normalized
 * {@link PublishedSpaceResolution} (metadata + flat file list). The folder tree
 * is derived from the files' `path`s client-side (see `useSpaceTree`).
 *
 * @throws AxiosError 404 when the space is not published or does not exist.
 */
async function resolvePublishedSpace(
  spaceUid: string
): Promise<PublishedSpaceResolution> {
  const token = await issueSpaceGuestToken(spaceUid);
  // Authenticate the snapshot with the bearer token rather than the cookie: the
  // guest cookie is `SameSite=None` and thus third-party (blocked) whenever the
  // app isn't served from the API host — e.g. local dev — which would 401 this
  // XHR. Browser-native thumbnail/download requests still rely on the cookie
  // (they can't send a header) and so work first-party in production.
  const { data } = await apiClientDriveV3.get<RawSpaceSnapshot>(
    `/${spaceUid}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const files = data.files ?? [];
  // A published space's file list interleaves directory entries
  // (`inode/directory`) with files; the folder count must exclude them.
  const fileCount = files.filter(
    (f) => f.mimeType !== "inode/directory"
  ).length;
  // The space DTO carries no last-modified timestamp of its own, so "last
  // updated" is derived as the most recent file modification — i.e. when the
  // space's contents last changed — falling back to the used-volume correction
  // time for a space with no dated files.
  const latestFileModified = files.reduce<number>((max, f) => {
    const t = f.modifiedAt ? new Date(f.modifiedAt).getTime() : NaN;
    return Number.isFinite(t) && t > max ? t : max;
  }, 0);
  const modifiedAt =
    latestFileModified > 0
      ? new Date(latestFileModified).toISOString()
      : data.usedVolumeCorrectAt;
  return {
    files,
    spaceName: data.name ?? "",
    provider: data.orgUid ? await fetchSpaceProvider(data.orgUid) : undefined,
    fileCount,
    modifiedAt,
    blurhash: data.blurhash,
  };
}

/**
 * Single-flight guest-token refresh for a published space: concurrent callers
 * join the one in-flight re-issue. Never rejects — it resolves to whether the
 * refresh succeeded — so each public wrapper applies its own failure policy.
 */
function runSpaceGuestTokenRefresh(spaceUid: string): Promise<boolean> {
  if (spaceGuestTokenRefreshInFlight) return spaceGuestTokenRefreshInFlight;
  spaceGuestTokenRefreshInFlight = (async () => {
    try {
      await issueSpaceGuestToken(spaceUid);
      return true;
    } catch {
      return false;
    } finally {
      spaceGuestTokenRefreshInFlight = null;
    }
  })();
  return spaceGuestTokenRefreshInFlight;
}

/**
 * Best-effort guest-cookie refresh for a published space — the space analogue
 * of {@link ensureFreshGuestToken}. Coalesces concurrent calls, skips when the
 * cookie was refreshed within {@link GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS}, and
 * never rejects. Call before an un-retryable action (a download click) or when
 * the tab becomes visible again.
 */
async function ensureFreshSpaceGuestToken(spaceUid: string): Promise<void> {
  if (
    !spaceGuestTokenRefreshInFlight &&
    performance.now() - lastSpaceGuestTokenRefreshAt <
      GUEST_TOKEN_REFRESH_MIN_INTERVAL_MS
  ) {
    return;
  }
  await runSpaceGuestTokenRefresh(spaceUid);
}

/**
 * Force-refresh the space guest cookie now (bypassing the throttle) and reject
 * on failure — the space analogue of {@link refreshGuestTokenNow}. Used to
 * recover from a `401` before retrying a preview/thumbnail load.
 */
async function refreshSpaceGuestTokenNow(spaceUid: string): Promise<void> {
  const refreshed = await runSpaceGuestTokenRefresh(spaceUid);
  if (!refreshed) {
    throw new Error("Failed to refresh the space guest-token cookie");
  }
}

/**
 * URL for the avatar of a published space. Authenticated via the guest cookie.
 */
function getSpaceAvatarUrl(spaceUid: string, height?: number): string {
  const base = `/${spaceUid}/avatar`;
  return apiClientDriveV3.getUri({
    url: height ? `${base}?height=${height}` : base,
  });
}

/**
 * URL for downloading the whole published space as a ZIP archive
 * (`GET /{spaceUid}/zip`). The endpoint archives the entire space — it has no
 * subset/selection parameter — so it's used for the "all files" download; a
 * partial selection falls back to per-file downloads. Cookie-authenticated.
 */
function getSpaceZipUrl(spaceUid: string): string {
  return apiClientDriveV3.getUri({ url: `/${spaceUid}/zip` });
}

/**
 * URL for downloading a single file from a published space.
 * Authenticated automatically via the guest-token cookie.
 */
function getSpaceFileDownloadUrl(
  spaceUid: string,
  metadataUid: string,
  { inline = false }: { inline?: boolean } = {}
): string {
  const url = `/${spaceUid}/files/${metadataUid}?inline=${
    inline ? "true" : "false"
  }`;
  return apiClientDriveV3.getUri({ url });
}

/**
 * URL for a pre-rendered thumbnail of a file in a published space
 * (`GET /{spaceUid}/files/{uid}/thumbnail-w200.jpg`), the cheap path for card
 * thumbnails. The card loads it with a native `<img>` and the endpoint sends an
 * ETag, so a changed file is revalidated without a cache-busting param — same
 * as the share-link thumbnail. `404` when the payload isn't renderable (callers
 * fall back to the file-type icon). Authenticated via the guest cookie.
 */
function getSpaceFileThumbnailUrl(
  spaceUid: string,
  metadataUid: string
): string {
  return apiClientDriveV3.getUri({
    url: `/${spaceUid}/files/${metadataUid}/thumbnail-w200.jpg`,
  });
}

/**
 * URL for a live-rendered preview of a file in a published space
 * (`GET /{spaceUid}/files/{uid}/preview?width=&height=`). Rendered on demand to
 * the requested box (aspect preserved). Used for the full-size preview overlay,
 * and as the fallback when {@link getSpaceFileThumbnailUrl} has no cached
 * thumbnail. Authenticated automatically via the guest-token cookie.
 */
function getSpaceFilePreviewUrl(
  spaceUid: string,
  metadataUid: string,
  { width, height }: { width?: number; height?: number } = {}
): string {
  const base = `/${spaceUid}/files/${metadataUid}/preview`;
  const query: string[] = [];
  if (width) query.push(`width=${width}`);
  if (height) query.push(`height=${height}`);
  const url = query.length ? `${base}?${query.join("&")}` : base;
  return apiClientDriveV3.getUri({ url });
}

// ==========================================================================
//                             Space invitations
// ==========================================================================

/**
 * Resolves an open space invitation for an invitee address. Unauthenticated.
 *
 * @throws AxiosError 404 when there is no open invitation for the pair (used,
 * revoked or never existed, indistinguishable by design).
 */
const fetchSpaceInvitationDetails = async (
  spaceUid: string,
  invitee: string
): Promise<SpaceInvitationDetails> => {
  const response = await apiClientDriveV3.get(
    `/invitation/${spaceUid}/details?invitee=${encodeURIComponent(invitee)}`
  );
  return response.data as SpaceInvitationDetails;
};

/**
 * URL for the avatar of the space an invitation points at. Unauthenticated;
 * meant as a plain <img> source with a glyph fallback on error.
 */
const getSpaceInvitationAvatarUrl = (
  spaceUid: string,
  invitee: string,
  height?: number
): string => {
  const base = `/invitation/${spaceUid}/avatar?invitee=${encodeURIComponent(
    invitee
  )}`;
  const url = height ? `${base}&height=${height}` : base;
  return apiClientDriveV3.getUri({ url });
};

/**
 * Resolves where an e-mail address can authenticate (login on its account's
 * host, signup on the free host, or nowhere). Unauthenticated.
 */
const fetchAuthNext = async (address: string): Promise<AuthNextResolution> => {
  const response = await apiClientForSkalioId.get(
    `/auth/next?address=${encodeURIComponent(address)}`
  );
  return response.data as AuthNextResolution;
};

type ListResponse<T, K extends string> = {
  total: number;
} & {
  [key in K]: T[];
};

export type { DownloadFileProp, ReservationCreationResult };

export {
  cancelReservation,
  confirmReservation,
  createMailboxReservation,
  fetchSkpEnvironment,
  fetchOrgEnvironment,
  fetchErrorMessageTranslationBundle,
  fetchPublicProfile,
  fetchReservation,
  getAvatarUri,
  getFileDownloadUrl,
  getFilePreviewUrl,
  getFilePreviewUrlV1,
  getTransferDownloadUrl,
  getUploadedFileSize,
  isLegacyUser,
  isSkalioIdUser,
  resolveVanityLink,
  uploadFileChunk,
  fetchMailboxGroup,
  updateReservation,
  getReservationToken,
  fetchTransfer,
  unlockTransfer2,
  resolveShareLink,
  ensureFreshGuestToken,
  refreshGuestTokenNow,
  getShareLinkAvatarUrl,
  fetchShareLinkAvatarBlobUrl,
  fetchShareLinkCreatorProfile,
  fetchShareLinkCreatorAvatarBlobUrl,
  getShareLinkFileDownloadUrl,
  getShareLinkFilePreviewUrl,
  getShareLinkFileThumbnailUrl,
  getShareLinkZipUrl,
  resolvePublishedSpace,
  ensureFreshSpaceGuestToken,
  refreshSpaceGuestTokenNow,
  getSpaceGuestToken,
  getSpaceAvatarUrl,
  getSpaceZipUrl,
  getSpaceFileDownloadUrl,
  getSpaceFileThumbnailUrl,
  getSpaceFilePreviewUrl,
  fetchSpaceInvitationDetails,
  getSpaceInvitationAvatarUrl,
  fetchAuthNext,
};
